import { useState, useRef, useCallback } from 'react';
import { message } from 'antd';
import { useRouter } from 'next/router';
import { getIsMobile } from 'lowcode/common/getDevice';
import { rewardRedeemCoupon } from '@/service';
import { copyText, navigateToUrl, getStoreUrl } from '../utils';
import { ERROR_CODE_MAP, RESET_RULES_ERROR_CODES } from '../constants';
import { CouponRule, RedeemParams, TranslationFn, RULE_TYPES } from '../types';

interface UseRedeemLogicProps {
    locale: string;
    pathUrl?: string;
    t: TranslationFn;
    guardLogin: (entryPosition?: number, buttonName?: string) => boolean;
    refreshAllRules: () => void;
    refershData?: () => void;
}

interface UseRedeemLogicReturn {
    // Loading state
    reedingLoading: boolean;
    // Confirm modal state
    currRules: CouponRule | null;
    setCurrRules: (rule: CouponRule | null) => void;
    // Success modal state
    couponModalOpen: boolean;
    setCouponModalOpen: (open: boolean) => void;
    currCoupon: CouponRule | null;
    setCurrCoupon: (coupon: CouponRule | null) => void;
    // Notes modal state
    currNotes: any;
    setCurrNotes: (notes: any) => void;
    // Ref for BuyForm
    buyFormRef: React.RefObject<any>;
    // Actions
    openConfirmModal: (rule: CouponRule) => void;
    onConfirm: () => Promise<void>;
    onRedeem: (rule: CouponRule) => Promise<void>;
    shopNow: (coupon: CouponRule) => void;
}

/**
 * Hook for handling redeem logic
 * Manages redeem flow, modals, and API calls
 */
export const useRedeemLogic = ({ locale, pathUrl, t, guardLogin, refreshAllRules, refershData }: UseRedeemLogicProps): UseRedeemLogicReturn => {
    const router = useRouter();
    const isMobile = getIsMobile();
    const buyFormRef = useRef<any>(null);

    // Loading state
    const [reedingLoading, setReedingLoading] = useState(false);

    // Confirm modal state
    const [currRules, setCurrRules] = useState<CouponRule | null>(null);

    // Success modal state
    const [couponModalOpen, setCouponModalOpen] = useState(false);
    const [currCoupon, setCurrCoupon] = useState<CouponRule | null>(null);

    // Notes modal state
    const [currNotes, setCurrNotes] = useState<any>(null);

    /**
     * Handle redeem error based on error code
     */
    const handleRedeemError = useCallback(
        (res: { code: string; message?: string }) => {
            const errorKey = ERROR_CODE_MAP[res?.code];

            if (errorKey) {
                message.error(t(errorKey) || res?.message);

                // Reset currRules for specific error codes
                if (RESET_RULES_ERROR_CODES.includes(res?.code)) {
                    setCurrRules(null);
                }
                return;
            }

            message.error(res?.message || t('network_error') || 'Network Error');
        },
        [t],
    );

    /**
     * Build redeem params from rule
     */
    const buildRedeemParams = useCallback(async (rule: CouponRule): Promise<RedeemParams> => {
        const { ruleId, version, ruleWebsite, ruleType } = rule;
        const ruleSiteVersion = ruleWebsite?.version;
        const params: RedeemParams = { ruleId, ruleVersion: version, ruleSiteVersion };

        // Add campaignVersion if it's a campaign rule
        if (rule?.campaignId) {
            params.campaignVersion = rule?.campaignVersion;
        }

        // Add address for physical products
        if (ruleType === RULE_TYPES.FREE_PRODUCTS) {
            const address = await buyFormRef?.current?.getFormData();
            params.address = address;
        }

        return params;
    }, []);

    /**
     * Open confirm modal (with login check)
     */
    const openConfirmModal = useCallback(
        (rule: CouponRule) => {
            if (!guardLogin(6)) return;
            setCurrRules(rule);
        },
        [guardLogin],
    );

    /**
     * Confirm handler for confirm modal
     */
    const onConfirm = useCallback(async () => {
        if (!currRules) return;
        await onRedeem(currRules);
        setCurrRules(null);
    }, [currRules]);

    /**
     * Core redeem logic
     */
    const onRedeem = useCallback(
        async (rule: CouponRule) => {
            // Login guard
            if (!guardLogin(0)) return;

            // Prevent double submit
            if (reedingLoading) return;

            setReedingLoading(true);

            try {
                const params = await buildRedeemParams(rule);
                const res = await rewardRedeemCoupon(params, locale);

                if (res?.code === '0') {
                    const data = res?.data || {};
                    setCouponModalOpen(true);
                    setCurrCoupon({ ...rule, ...data });

                    // Copy coupon code
                    copyText(res?.data?.coupon, t('site_account_profile_save_success'));

                    // Refresh data
                    refershData?.();
                    refreshAllRules();
                    return;
                }

                // Handle error
                handleRedeemError(res);
            } finally {
                setReedingLoading(false);
            }
        },
        [guardLogin, reedingLoading, buildRedeemParams, locale, t, refershData, refreshAllRules, handleRedeemError],
    );

    /**
     * Navigate to shop based on coupon type
     */
    const shopNow = useCallback(
        (coupon: CouponRule) => {
            const { thirdUrl, ruleType, jumpUrl } = coupon;

            // Third-party coupon
            if (ruleType === RULE_TYPES.THIRD_COUPONS && thirdUrl) {
                navigateToUrl(thirdUrl, isMobile, router);
                return;
            }

            // Regular coupon with jumpUrl
            if (ruleType === RULE_TYPES.COUPONS && jumpUrl) {
                navigateToUrl(jumpUrl, isMobile, router);
                return;
            }

            // Default: go to store
            navigateToUrl(getStoreUrl(locale), isMobile, router);
        },
        [isMobile, router, locale],
    );

    return {
        reedingLoading,
        currRules,
        setCurrRules,
        couponModalOpen,
        setCouponModalOpen,
        currCoupon,
        setCurrCoupon,
        currNotes,
        setCurrNotes,
        buyFormRef,
        openConfirmModal,
        onConfirm,
        onRedeem,
        shopNow,
    };
};
